"use client";

import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { Loader2 } from "lucide-react";
import FingerprintJS from '@fingerprintjs/fingerprintjs';

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getEmailValidationError } from "@/lib/emailValidation";
import { AccountNotFoundCopy } from "@/components/account-not-found-copy";
import { AccountDeletedCopy } from "@/components/account-deleted-copy";
import { isAccountDeletedError, isAccountNotFoundError, isCredentialMismatchError } from "@/lib/user-error";


const loginSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address." }),
  password: z.string().min(1, { message: "Password is required." }),
});

type LoginData = z.infer<typeof loginSchema>;

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** @deprecated Signup lives on /register; this modal is login-only. */
  defaultMode?: "login" | "signup";
  title?: string;
  description?: string;
}

export function AuthModal({
  isOpen,
  onClose,
  title,
  description,
}: AuthModalProps) {
  const loginForm = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const [loginEmailError, setLoginEmailError] = useState("");
  const loginEmailValue = loginForm.watch("email");

  useEffect(() => {
    setLoginEmailError(getEmailValidationError(loginEmailValue));
  }, [loginEmailValue]);

  const resetForm = (): void => {
    loginForm.reset();
    setError("");
    setIsLoading(false);
  };

  const handleClose = (): void => {
    resetForm();
    onClose();
  };

  const getFingerprint = async (): Promise<string> => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  };

  const handleLogin = async (data: LoginData): Promise<void> => {
    setIsLoading(true);
    setError("");

    try {
      const fingerprint = await getFingerprint();
      const result = await signIn('credentials', {
        redirect: false,
        email: data.email,
        password: data.password,
        fingerprint,
      });
      
      if (result?.error) {
        if (isAccountDeletedError(result.error)) {
          throw new Error("ACCOUNT_DELETED");
        }
        if (isAccountNotFoundError(result.error)) {
          throw new Error("ACCOUNT_NOT_FOUND");
        }
        const friendly = isCredentialMismatchError(result.error)
          ? "Incorrect email or password. Please try again."
          : "Something went wrong logging in. Please try again.";
        throw new Error(friendly);
      }
      
      toast({ title: 'Login successful!' });
      handleClose();
    } catch (err: any) {
      setError(
        err.message === "ACCOUNT_NOT_FOUND" ||
          err.message === "ACCOUNT_DELETED" ||
          err.message?.includes("Incorrect") ||
          err.message?.includes("verify")
          ? err.message
          : "Something went wrong logging in. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm bg-white text-black rounded-lg max-h-[90vh] overflow-y-auto mt-8 sm:mt-12">
        <DialogHeader>
          <DialogTitle>
            {title || "Login"}
          </DialogTitle>
          <DialogDescription>
            {description || "Enter your credentials to access your account"}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              {...loginForm.register("email")}
            />
            {(loginForm.formState.errors.email || loginEmailError) && (
              <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email?.message || loginEmailError}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              {...loginForm.register("password")}
            />
            {loginForm.formState.errors.password && (
              <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-500">
              {isAccountDeletedError(error) ? (
                <AccountDeletedCopy />
              ) : isAccountNotFoundError(error) ? (
                <AccountNotFoundCopy email={loginEmailValue} />
              ) : (
                error
              )}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={isLoading || !!loginEmailError}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Login
          </Button>
        </form>
        
        <div className="text-center text-sm text-gray-500">
          <p>
            Don&apos;t have an account?{" "}
            <Link href="/register" className="text-blue-600 hover:underline" onClick={handleClose}>
              Sign up
            </Link>
          </p>
          <p className="mt-2">
            <Link href="/forgot-password" className="text-blue-600 hover:underline text-sm">Forgot Password?</Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
